import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DatePicker, { registerLocale } from 'react-datepicker';
import { de } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import './AppointmentBooking.css';
import { toast } from 'sonner';

// Register German locale
registerLocale('de', de);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AppointmentBooking = ({ currentUserId, currentUserName, onBookingComplete }) => {
  const [vertriebsleiterList, setVertriebsleiterList] = useState([]);
  const [selectedVertriebsleiter, setSelectedVertriebsleiter] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);

  useEffect(() => {
    fetchVertriebsleiterList();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSelectedDate(tomorrow);
  }, []);

  useEffect(() => {
    if (selectedVertriebsleiter && selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedVertriebsleiter, selectedDate]);

  useEffect(() => {
    if (selectedVertriebsleiter) {
      fetchAvailableDates();
    }
  }, [selectedVertriebsleiter]);
  
  const formatDateForAPI = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchVertriebsleiterList = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/appointments/availability`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      setVertriebsleiterList(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedVertriebsleiter(response.data[0].vertriebsleiter_id);
      }
    } catch (error) {
      console.error('Error fetching Vertriebsleiter:', error);
      setVertriebsleiterList([]);
    }
  };

  const fetchAvailableSlots = async () => {
    try {
      setLoading(true);
      setError(null);
      const dateStr = formatDateForAPI(selectedDate);
      const response = await axios.get(
        `${BACKEND_URL}/api/appointments/available-slots/${selectedVertriebsleiter}`,
        {
          params: { date: dateStr },
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setAvailableSlots(response.data.available_slots || []);
    } catch (error) {
      let errorMessage = 'Fehler beim Laden der verfuegbaren Termine';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg).join(', ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
      }
      setError(errorMessage);
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDates = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/appointments/available-dates/${selectedVertriebsleiter}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      setAvailableDates(response.data.dates || []);
    } catch {
      setAvailableDates([]);
    }
  };

  const handleSlotClick = (slot) => {
    setSelectedSlot(slot);
    setShowConfirmModal(true);
  };

  const handleConfirmBooking = async () => {
    if (!selectedSlot) return;
    try {
      setBooking(true);
      setError(null);
      await axios.post(
        `${BACKEND_URL}/api/appointments/appointments`,
        {
          vertriebsleiter_id: selectedVertriebsleiter,
          vertriebler_id: currentUserId,
          appointment_date: selectedSlot.start_time,
          duration_minutes: 15,
          notes: notes
        },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      setShowConfirmModal(false);
      toast.success('Termin erfolgreich gebucht! Sie erhalten eine Bestaetigungs-E-Mail mit dem Zoom-Link.');
      if (onBookingComplete) onBookingComplete();
    } catch (error) {
      let errorMessage = 'Fehler beim Buchen des Termins';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => `${err.loc ? err.loc.join(' -> ') : 'Unbekannt'}: ${err.msg}`).join(', ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        }
      }
      setError(errorMessage);
      // Refresh slots if booking failed due to already booked slot
      if (selectedVertriebsleiter && selectedDate) {
        fetchAvailableSlots();
      }
    } finally {
      setBooking(false);
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 60);
    return maxDate;
  };

  const availableDateSet = new Set(availableDates.map(d => d.date));
  const availableDateMap = Object.fromEntries(availableDates.map(d => [d.date, d.free_slots]));

  const isDateAvailable = (date) => {
    const dateStr = formatDateForAPI(date);
    return availableDateSet.has(dateStr);
  };

  const filterDate = (date) => {
    return isDateAvailable(date);
  };

  const getDayClassName = (date) => {
    if (isDateAvailable(date)) return 'available-date';
    return 'unavailable-date';
  };

  // Filter out past time slots (safety check)
  const filteredSlots = availableSlots.filter(slot => {
    return new Date(slot.start_time) > new Date();
  });

  // Find next available date
  const nextAvailableDate = availableDates.length > 0
    ? new Date(availableDates[0].date + 'T00:00:00')
    : null;

  const selectedVLName = vertriebsleiterList.find(
    vl => vl.vertriebsleiter_id === selectedVertriebsleiter
  )?.vertriebsleiter_name || '';

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Abschlussgespräch buchen</h2>
        <p className="text-gray-600">Wählen Sie einen Vertriebsleiter und einen verfügbaren Termin</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">{error}</div>
      )}

      <div className="space-y-6">
        {/* Vertriebsleiter Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Vertriebsleiter auswählen</label>
          <select
            value={selectedVertriebsleiter || ''}
            onChange={(e) => setSelectedVertriebsleiter(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="vl-select"
          >
            {vertriebsleiterList.map(vl => (
              <option key={vl.vertriebsleiter_id} value={vl.vertriebsleiter_id}>{vl.vertriebsleiter_name}</option>
            ))}
          </select>
        </div>

        {/* Date Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Datum auswählen</label>
          {availableDates.length > 0 && (
            <div className="mb-3 flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>
                Verfügbar
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-gray-200 inline-block"></span>
                Keine Termine
              </span>
              {nextAvailableDate && (
                <button
                  onClick={() => { setSelectedDate(nextAvailableDate); setSelectedSlot(null); }}
                  className="text-blue-600 hover:text-blue-800 font-medium underline"
                  data-testid="jump-to-next-available"
                >
                  Nächster freier Tag: {nextAvailableDate.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' })}
                </button>
              )}
            </div>
          )}
          <DatePicker
            selected={selectedDate}
            onChange={(date) => { setSelectedDate(date); setSelectedSlot(null); }}
            minDate={getMinDate()}
            maxDate={getMaxDate()}
            filterDate={filterDate}
            dateFormat="dd.MM.yyyy"
            locale="de"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
            calendarClassName="modern-calendar"
            inline
            dayClassName={getDayClassName}
            showPopperArrow={false}
            placeholderText="Wählen Sie ein Datum"
          />
        </div>

        {/* Available Time Slots */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Verfügbare Zeitslots</label>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredSlots.length === 0 ? (
            <div className="text-center py-8 text-gray-500 border border-gray-200 rounded-lg">
              <p>Keine verfügbaren Termine für diesen Tag</p>
              <p className="text-sm mt-2">Bitte wählen Sie ein anderes Datum</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {filteredSlots.map((slot, index) => (
                <button
                  key={index}
                  onClick={() => handleSlotClick(slot)}
                  data-testid={`slot-${index}`}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    selectedSlot?.start_time === slot.start_time
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                  }`}
                >
                  {formatTime(slot.start_time)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Booking Confirmation Modal ── */}
      {showConfirmModal && selectedSlot && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-5">
              <h3 className="text-white font-bold text-lg">Termin bestätigen</h3>
              <p className="text-blue-100 text-sm mt-1">Bitte überprüfen Sie die Details</p>
            </div>
            <div className="p-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5 space-y-2">
                <div className="flex items-center gap-3 text-sm text-blue-900">
                  <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span><strong>Vertriebsleiter:</strong> {selectedVLName}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-blue-900">
                  <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="18" rx="2" strokeWidth={2} /><line x1="16" y1="2" x2="16" y2="6" strokeWidth={2} /><line x1="8" y1="2" x2="8" y2="6" strokeWidth={2} /><line x1="3" y1="10" x2="21" y2="10" strokeWidth={2} />
                  </svg>
                  <span><strong>Datum:</strong> {selectedDate.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-blue-900">
                  <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth={2} /><polyline points="12 6 12 12 16 14" strokeWidth={2} />
                  </svg>
                  <span><strong>Uhrzeit:</strong> {formatTime(selectedSlot.start_time)} – {formatTime(selectedSlot.end_time)} Uhr</span>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notiz hinzufügen <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="z.B. Fragen zum Gesprächsablauf, besondere Anliegen..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="booking-notes-input"
                />
              </div>

              <p className="text-xs text-gray-400 mb-5">
                Sie erhalten eine Bestätigungs-E-Mail mit dem Zoom-Meeting-Link.
              </p>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => { setShowConfirmModal(false); setSelectedSlot(null); }}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="cancel-booking-btn"
              >
                Abbrechen
              </button>
              <button
                onClick={handleConfirmBooking}
                disabled={booking}
                className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="confirm-booking-btn"
              >
                {booking ? (
                  <>
                    <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"/>
                    Wird gebucht...
                  </>
                ) : 'Termin verbindlich buchen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentBooking;
